import { useMemo } from "react";
import { ColumnDef } from "@tanstack/react-table";
import { Pencil, Plus } from "lucide-react";
import { DateTimeColumn } from "@/components/tableColumns/DateTimeColumn";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/badges/Badge";
import {
	StructurePropertyConfig,
	StructurePropertyConfigCategory,
	StructurePropertyListItem,
} from "@/endpoints/dryp/structureProperty";

// Tailwind-friendly tinted palette per calc-config category. Used for the
// "Used in" chips, which describe which calculation a property feeds — not
// the property itself, so they don't follow the per-property `color`.
const CATEGORY_CHIP_CLASSES: Record<StructurePropertyConfigCategory, string> = {
	IAndI: "bg-sky-500/10 text-sky-700 border-sky-500/20 dark:text-sky-300",
};

// "PipeDiameterInMillimeters" → "Pipe Diameter In Millimeters"
const formatPascalName = (name: string): string =>
	name
		.replace(/([a-z0-9])([A-Z])/g, "$1 $2")
		.replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
		.trim();

const formatLimits = (minLimit: number | null, maxLimit: number | null, unit: string | null): string | null => {
	if (minLimit === null && maxLimit === null) return null;
	const unitSuffix = unit ? ` ${unit}` : "";
	if (minLimit !== null && maxLimit !== null) return `${minLimit}–${maxLimit}${unitSuffix}`;
	if (minLimit !== null) return `≥ ${minLimit}${unitSuffix}`;
	return `≤ ${maxLimit}${unitSuffix}`;
};

// Tinted-circle avatar for a property — uses `color` as the accent and falls
// back to a muted swatch when null. Renders `symbol` (e.g. "PC", "∅", "MNV")
// or the first 2 letters of typeName if there's no symbol.
type PropertyAvatarProps = {
	symbol: string | null;
	color: string | null;
	typeName: string;
};

const PropertyAvatar = ({ symbol, color, typeName }: PropertyAvatarProps) => {
	const label = (symbol?.trim() || typeName.replace(/[^A-Za-z]/g, "").slice(0, 2)).slice(0, 3);
	const tintedBackground = color ? `${color}1A` : undefined; // 1A ≈ 10% alpha
	return (
		<Avatar className="h-8 w-8 rounded-md shrink-0 ring-1 ring-border/40">
			<AvatarFallback
				className="rounded-md text-xs font-semibold tabular-nums"
				style={color ? { backgroundColor: tintedBackground, color } : undefined}
			>
				{label}
			</AvatarFallback>
		</Avatar>
	);
};

export function usePropertyColumns(
	onAddOrUpdate: (property: StructurePropertyListItem) => void,
): ColumnDef<StructurePropertyListItem>[] {
	return useMemo(
		() => [
			{
				accessorKey: "typeName",
				header: "Property",
				cell: ({ row }) => {
					const property = row.original;
					return (
						<div className="flex items-center gap-3 min-w-0 py-1">
							<PropertyAvatar
								symbol={property.symbol}
								color={property.color}
								typeName={property.typeName}
							/>
							<div className="flex flex-col min-w-0">
								<span className="font-medium text-foreground truncate">
									{formatPascalName(property.typeName)}
								</span>
								{property.description && (
									<span className="text-[11px] text-muted-foreground">{property.description}</span>
								)}
							</div>
						</div>
					);
				},
			},
			{
				id: "categories",
				header: "Used in",
				enableSorting: false,
				cell: ({ row }) => {
					const configEntries = row.original.config ?? [];
					if (configEntries.length === 0) {
						return <span className="text-xs text-muted-foreground italic">—</span>;
					}
					return (
						<div className="flex flex-wrap gap-1">
							{configEntries.map((configEntry: StructurePropertyConfig) => (
								<Badge
									key={configEntry.category}
									variant="outline"
									className={`text-[10px] py-0.5 ${CATEGORY_CHIP_CLASSES[configEntry.category] ?? CATEGORY_CHIP_CLASSES.IAndI}`}
								>
									{configEntry.category === "IAndI" ? "I&I" : configEntry.category}
									{configEntry.isRequired && (
										<span
											className="ml-0.5 inline-block h-1.5 w-1.5 rounded-full bg-current"
											aria-label="required"
										/>
									)}
								</Badge>
							))}
						</div>
					);
				},
			},
			{
				accessorKey: "value",
				id: "value",
				header: "Current value",
				cell: ({ row }) => {
					const { value, unit } = row.original;
					if (value === null) return <span className="text-muted-foreground">—</span>;
					return (
						<span className="text-sm font-medium tabular-nums">
							{value}
							{unit && <span className="ml-1 text-muted-foreground font-normal">{unit}</span>}
						</span>
					);
				},
			},
			{
				id: "limits",
				header: "Limits",
				enableSorting: false,
				cell: ({ row }) => {
					const property = row.original;
					const limitsLabel = formatLimits(property.minLimit, property.maxLimit, property.unit);
					if (!limitsLabel) return <span className="text-muted-foreground">—</span>;
					return <span className="text-xs text-muted-foreground tabular-nums">{limitsLabel}</span>;
				},
			},
			DateTimeColumn<StructurePropertyListItem>({
				accessorKey: "updatedDateTime",
				id: "updatedDateTime",
				header: "Last updated",
				format: "dd LLL yyyy, HH:mm",
				align: "left",
				datetimeClassName: "text-sm",
			}),
			{
				id: "actions",
				header: "",
				enableSorting: false,
				cell: ({ row }) => {
					const property = row.original;
					const isEditing = property.value !== null;
					return (
						<div className="flex justify-end">
							<Button
								variant="outline"
								size="sm"
								className="h-7 gap-1 px-2 text-xs"
								onClick={() => onAddOrUpdate(property)}
							>
								{isEditing ? (
									<>
										<Pencil className="h-3.5 w-3.5" />
										Edit
									</>
								) : (
									<>
										<Plus className="h-3.5 w-3.5" />
										Add
									</>
								)}
							</Button>
						</div>
					);
				},
			},
		],
		[onAddOrUpdate],
	);
}
